import type { ReactNode } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { ReviewComment, SubmittedCode } from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Code section (spec §8.5) — the client's own submitted code, off the
 * submission artifact (`code`, mapped by `artifactCode` in live-review.ts).
 * It rendered a hardcoded LIS solution for every client until LG-517; there
 * is no fallback listing now, because a coach reading someone else's code as
 * this client's is the failure this section exists to avoid.
 *
 * Line numbers are 1-based indices into that listing, and they are load-
 * bearing: clicking one anchors a comment to it (`ReviewComment.lines`), and
 * the anchor has to point at the same line when the review is reopened.
 */

export function CodeSection(props: {
  code: SubmittedCode | null
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { code, comments = [], anchorRef } = props
  const { composer, openComposer, openLineComposer, extendComposerLines } =
    useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'code')

  // The open composer's anchor is the single source of the highlighted range —
  // no local selection state to drift out of sync with it.
  const selected =
    composer != null && composer.section === 'code'
      ? (composer.lines ?? null)
      : null

  // Source-filtered, because the tint is the AI's colour and says so: a coach's
  // own line comment comes back from a reload carrying `lines` too (LG-517 is
  // what makes coach anchors persist), and painting it purple would tell the
  // coach the AI flagged code they flagged themselves.
  const aiLines = sectionComments.filter(
    (c): c is ReviewComment & { lines: [number, number] } =>
      c.lines != null && c.source === 'ai',
  )

  function handleLineClick(n: number) {
    // Extend only when the open code composer is already anchored to lines. A
    // code composer opened via `+ Comment` targets `Code · Overall` and holds
    // no anchor, so there is nothing to widen; retarget it onto the clicked
    // line instead (matching the .dc reference, which only extends when a line
    // selection exists). That retarget starts a new composing session, which
    // is what clears any draft written about the section as a whole.
    if (selected) {
      extendComposerLines(n)
    } else {
      openLineComposer('code', 'Code · ', 'suggestion', [n, n])
    }
  }

  const hint = selected
    ? `Selected L${selected[0]}${
        selected[1] !== selected[0] ? `–L${selected[1]}` : ''
      } — click another line to extend the range.`
    : 'Click a line number to comment on specific lines.'

  return (
    <SectionCard
      title="Code"
      commentCount={sectionComments.length}
      onAddComment={() => openComposer('code', 'Code · Overall', 'suggestion')}
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      {code === null ? (
        <div
          className="rounded-[10px] border border-dashed border-(--cd-border-3) p-4 text-center"
          style={{
            background: 'color-mix(in oklab, var(--cd-bg) 50%, transparent)',
          }}
        >
          <div className="text-[13px] font-semibold text-(--cd-text-2)">
            No code in this submission
          </div>
          <p className="mt-1 text-xs text-(--cd-text-muted)">
            The submitted artifact recorded no final code, so there is nothing
            to read here — and nothing to comment on by line.
          </p>
        </div>
      ) : (
        <>
          {/* Language and result are the artifact's own, or absent: the
              summary line is skipped entirely when the snapshot stored no
              execution summary, rather than rendering a 0/0. */}
          {(code.language != null || code.testSummary != null) && (
            <div className="mb-2 flex items-baseline gap-2 text-[11.5px]">
              {code.testSummary != null && (
                <span className="text-(--cd-text-2)">
                  Final submission · {code.testSummary}
                </span>
              )}
              {code.language != null && (
                <span className="ml-auto font-mono text-(--cd-text-faint)">
                  {code.language}
                </span>
              )}
            </div>
          )}
          <div className="mb-2.5 text-[11.5px] text-(--cd-text-faint)">
            {hint}
          </div>
          {/* The listing length is the client's to decide, so it is bounded
              here: a long submission scrolls inside the card instead of
              pushing every section below it off the screen. */}
          <div className="max-h-[32rem] overflow-auto rounded-[10px] border border-(--cd-border-2) bg-(--cd-bg) py-2.5">
            {code.lines.map((text, idx) => {
              const n = idx + 1
              const isSelected =
                selected != null && n >= selected[0] && n <= selected[1]
              const isAi =
                !isSelected &&
                aiLines.some((c) => n >= c.lines[0] && n <= c.lines[1])
              return (
                <button
                  key={n}
                  type="button"
                  // The code is the point of this section, so it has to be in
                  // the accessible name: a bare `Line ${n}` label REPLACES the
                  // contents, leaving a screen-reader user clicking through
                  // numbered buttons that never say what they contain.
                  // `aria-pressed` carries the selection that the green tint
                  // otherwise conveys by colour alone.
                  aria-label={`Line ${n}: ${text}`}
                  aria-pressed={isSelected}
                  onClick={() => handleLineClick(n)}
                  className="flex w-full cursor-pointer text-left"
                  style={{
                    background: isSelected
                      ? 'color-mix(in oklab, var(--cd-green) 12%, transparent)'
                      : isAi
                        ? 'color-mix(in oklab, var(--cd-purple) 8%, transparent)'
                        : 'transparent',
                  }}
                >
                  <span
                    className="w-10 shrink-0 select-none pr-3.5 text-right font-mono text-xs leading-[1.75]"
                    style={{
                      color: isSelected
                        ? 'var(--cd-green)'
                        : isAi
                          ? 'var(--cd-purple)'
                          : 'var(--cd-text-ghost)',
                    }}
                  >
                    {n}
                  </span>
                  <span className="whitespace-pre pr-4 font-mono text-[13px] leading-[1.62] text-(--cd-text-2)">
                    {text}
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </SectionCard>
  )
}
